import { Node } from '@xyflow/react';

import { SectionLabelProps } from '@/components/diagrams/FlowChart/nodes/SectionLabelNode/SectionLabelNode';
import { AfterDeath, EntityKind, TileNotesInput } from '@/types/schema';

export type SectionLabelNode = Node<SectionLabelProps, 'sectionLabel'>;

export const TILE_NOTES_PROMPT_IDS = [
  'trustees-access',
  'dispositive-provisions',
  'holdings-liquidity',
  'custom',
] as const;

export type TileNotesPromptId = (typeof TILE_NOTES_PROMPT_IDS)[number];

export interface TileNotesPromptOption {
  value: TileNotesPromptId;
  display: string;
}

export const TILE_NOTES_PROMPT_OPTIONS: TileNotesPromptOption[] = [
  { value: 'trustees-access', display: 'Trustees & current beneficiaries' },
  { value: 'dispositive-provisions', display: 'Dispositive provisions' },
  { value: 'holdings-liquidity', display: 'Holdings and liquidity profile' },
  { value: 'custom', display: 'Custom' },
];

export const TILE_NOTES_PROMPT_TEXTS: Partial<
  Record<TileNotesPromptId, string>
> = {
  'trustees-access':
    "Summarize who controls this trust and what distributions are available. Include the grantor's access during life, HEMS or other distribution standards, trustee succession, and any age-based control transitions for beneficiaries. Use bullet points to list each key role and access provision.",
  'dispositive-provisions':
    'Describe how assets flow at each death event. Cover pour-over and funding provisions, what trusts or shares are created, how remainder assets divide among beneficiaries, and any powers of appointment. Use bullet points to summarize each death event and asset flow step.',
  'holdings-liquidity':
    "Characterize the trust's asset composition and liquidity. Summarize the mix of cash, securities, real estate, and other holdings, noting any illiquid positions or inter-family loans. Provide a sense of overall portfolio size and flexibility. Use bullet points to break down each asset category and liquidity characteristic.",
};

export const SUPPORTED_TILE_NOTES_ENTITY_KINDS: EntityKind[] = [
  EntityKind.RevocableTrust,
  EntityKind.IrrevocableTrust,
  EntityKind.SlatTrust,
  EntityKind.IlitTrust,
  EntityKind.GratTrust,
  EntityKind.QprtTrust,
  EntityKind.CltTrust,
  EntityKind.CrtTrust,
  EntityKind.LlcBusinessEntity,
  EntityKind.LpBusinessEntity,
  EntityKind.GpBusinessEntity,
  EntityKind.SoleProprietorshipBusinessEntity,
  EntityKind.ScorpBusinessEntity,
  EntityKind.CcorpBusinessEntity,
];

export function isSupportedTileNotesEntityKind(kind: EntityKind): boolean {
  return SUPPORTED_TILE_NOTES_ENTITY_KINDS.includes(kind);
}

/**
 * Minimal shape of an entity-graph node needed to build a TileNotesInput.
 * Both the waterfall (EstateWaterfall_NodeFragment['node']) and entity-diagram
 * (EntityDiagram_NodeFragment['node']) node types are assignable to this.
 */
export interface TileNotesEntityNode {
  __typename?: string;
  id: string;
  entityKind?: EntityKind | null;
}

/**
 * Shapes a single entity-graph node into a TileNotesInput. Shared by the
 * waterfall and entity-diagram single-tile builders so the Entity-vs-
 * TestamentaryEntity payload rules live in one place. Returns null for nodes
 * that can't generate notes: unsupported entity kinds and non-entity nodes
 * (individuals, organizations).
 */
export function entityNodeToTileNotesInput(
  node: TileNotesEntityNode,
  tileID: string,
  deathSection: AfterDeath
): TileNotesInput | null {
  if (node.__typename === 'Entity') {
    if (!node.entityKind || !isSupportedTileNotesEntityKind(node.entityKind)) {
      return null;
    }
    return { tileID, deathSection, entityIDs: [node.id] };
  }

  if (node.__typename === 'TestamentaryEntity') {
    return { tileID, deathSection, testamentaryEntityIDs: [node.id] };
  }

  return null;
}
